/**
 * Le tableau de bord des carrières : ce que chaque carrière doit sortir, et quand.
 *
 * *Demande du métier, 16/08/2026 : « une présentation en ligne par carrière avec numéro
 * et libellé carrière, puis en ligne les articles et par APO avec libellé APO, avec un
 * visuel planning jour par jour, le tonnage indiqué dans la cellule du jour et l'heure
 * indiquée à côté du tonnage. Par défaut on doit voir le total par jour et par article,
 * puis quand on déplie on doit voir par article le détail des chantiers (APO). En
 * colonne il doit y avoir un total par semaine puis un total par mois ; par défaut tu
 * laisses les deux prochaines semaines en jour par jour et à partir de la 3ᵉ semaine tu
 * fermes la présentation jour par jour. Enfin tu mets une dernière colonne avec le total
 * général des lignes. »*
 *
 * ## Trois niveaux de lignes, deux visibles
 *
 * Carrière → article → chantier. Les deux premiers s'affichent d'emblée, le troisième au
 * dépli : le responsable carrières commande une production, pas des chantiers. Il ouvre
 * l'article quand il veut savoir **pour qui** — c'est-à-dire quand il doit arbitrer.
 *
 * ## Deux replis indépendants, et c'est délibéré
 *
 * Les lignes se déplient par article, les colonnes par semaine. Les lier — « déplier une
 * semaine déplie ses chantiers » — mêlerait deux questions : *qui commande quoi* et
 * *quand*. On regarde souvent le détail d'un article sur un horizon replié, et l'inverse
 * est tout aussi courant.
 *
 * ## Le total d'un mois ne se calcule pas sur ses semaines
 *
 * Une semaine à cheval sur deux mois appartient aux deux. Les totaux mensuels portent
 * donc sur les JOURS du mois, jamais sur la somme des colonnes de semaine affichées à
 * côté — sinon la dernière semaine d'août compterait ses jours de septembre dans août, et
 * la somme des mois dépasserait le total général sans que rien ne le signale.
 */
import { Fragment, useMemo, useState } from "react";
import type { JSX } from "react";
import {
  ajouterJours,
  dec,
  formaterDateCourte,
  formaterNombre,
  jourDeLaSemaine,
} from "@planif/moteur";
import type { BesoinDate, Carriere, ChantierAuPlan } from "./api";

/* ---------------------------------------------------------------------------
 * Les colonnes
 * ------------------------------------------------------------------------- */

export type Colonne =
  | { readonly genre: "jour"; readonly cle: string; readonly jours: readonly string[] }
  | {
      readonly genre: "semaine" | "mois" | "total";
      readonly cle: string;
      readonly libelle: string;
      readonly jours: readonly string[];
    };

/**
 * Le lundi de la semaine d'une date — la clé qui regroupe les jours.
 *
 * Par l'arithmétique du moteur et **sans aucun objet de date**, comme partout dans le
 * produit : `new Date(…)` emporterait une heure, donc un fuseau, et le lundi basculerait
 * d'un jour selon l'endroit d'où l'on regarde. C'est le défaut qui avait fait compter
 * 254 jours ouvrés au lieu de 252 sur l'autre application du groupe.
 */
function lundiDe(date: string): string {
  return ajouterJours(date, -(jourDeLaSemaine(date) - 1));
}

const MOIS = [
  "janvier",
  "février",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "août",
  "septembre",
  "octobre",
  "novembre",
  "décembre",
] as const;

function libelleDuMois(cle: string): string {
  return `${MOIS[Number(cle.slice(5, 7)) - 1] ?? ""} ${cle.slice(0, 4)}`;
}

/**
 * Les colonnes du tableau, dans l'ordre chronologique.
 *
 * Une semaine dépliée donne ses jours **puis** son total ; une semaine repliée, son
 * total seul. Le total d'un mois s'insère après la dernière semaine qui porte un de ses
 * jours — donc au bon endroit dans la lecture, et non rejeté en fin de tableau où il
 * faudrait faire défiler trois mois pour le trouver.
 */
export function colonnesDe(
  jours: readonly string[],
  semainesOuvertes: ReadonlySet<string>,
): readonly Colonne[] {
  const parSemaine = new Map<string, string[]>();
  const parMois = new Map<string, string[]>();
  for (const jour of jours) {
    const semaine = lundiDe(jour);
    parSemaine.set(semaine, [...(parSemaine.get(semaine) ?? []), jour]);
    const mois = jour.slice(0, 7);
    parMois.set(mois, [...(parMois.get(mois) ?? []), jour]);
  }

  /**
   * Les mois qui se terminent sur chaque semaine — **plusieurs, pas un**.
   *
   * La semaine du 31 août porte le dernier jour d'août ET des jours de septembre. Une
   * seule entrée par lundi ferait écraser août par septembre, et la colonne « août »
   * n'existerait nulle part : son total disparaîtrait du tableau sans qu'aucun chiffre
   * ne paraisse faux.
   */
  const finDuMois = new Map<string, string[]>();
  for (const [mois, joursDuMois] of [...parMois.entries()].sort((a, b) =>
    a[0].localeCompare(b[0]),
  )) {
    const dernier = joursDuMois[joursDuMois.length - 1];
    if (dernier === undefined) continue;
    const lundi = lundiDe(dernier);
    finDuMois.set(lundi, [...(finDuMois.get(lundi) ?? []), mois]);
  }

  const colonnes: Colonne[] = [];
  for (const [semaine, joursDeLaSemaine] of [...parSemaine.entries()].sort((a, b) =>
    a[0].localeCompare(b[0]),
  )) {
    if (semainesOuvertes.has(semaine)) {
      for (const jour of joursDeLaSemaine) {
        colonnes.push({ genre: "jour", cle: jour, jours: [jour] });
      }
    }
    colonnes.push({
      genre: "semaine",
      cle: `s-${semaine}`,
      libelle: `sem. ${formaterDateCourte(semaine).slice(-5)}`,
      jours: joursDeLaSemaine,
    });
    for (const mois of finDuMois.get(semaine) ?? []) {
      colonnes.push({
        genre: "mois",
        cle: `m-${mois}`,
        libelle: libelleDuMois(mois),
        jours: parMois.get(mois) ?? [],
      });
    }
  }

  colonnes.push({ genre: "total", cle: "total", libelle: "Total", jours });
  return colonnes;
}

/**
 * Les semaines ouvertes par défaut : celle d'aujourd'hui et la suivante.
 *
 * *« Par défaut tu laisses les deux prochaines semaines en jour par jour et à partir de
 * la 3ᵉ semaine tu fermes la présentation jour par jour. »*
 *
 * Les semaines passées sont donc repliées elles aussi : on garde J-7 pour retrouver ce
 * qui vient d'être livré, pas pour le lire jour par jour.
 */
export function semainesParDefaut(aujourdhui: string): ReadonlySet<string> {
  const lundi = lundiDe(aujourdhui);
  return new Set([lundi, ajouterJours(lundi, 7)]);
}

/* ---------------------------------------------------------------------------
 * Les lignes
 * ------------------------------------------------------------------------- */

/** Ce qu'une cellule porte : un volume, et les heures auxquelles il est attendu. */
type Cellule = { quantite: string; heures: string[] };

type LigneChantier = {
  readonly chantierId: string;
  readonly apo: string;
  readonly nom: string;
  readonly cellules: ReadonlyMap<string, Cellule>;
};

type LigneArticle = {
  readonly code: string;
  readonly libelle: string;
  readonly unite: string;
  readonly chantiers: readonly LigneChantier[];
  readonly cellules: ReadonlyMap<string, Cellule>;
};

type LigneCarriere = {
  readonly cle: string;
  readonly code: string;
  readonly libelle: string;
  readonly articles: readonly LigneArticle[];
  readonly cellules: ReadonlyMap<string, Cellule>;
};

/** Ajoute un besoin dans une cellule, en cumulant le volume et en gardant les heures. */
function cumuler(cellules: Map<string, Cellule>, besoin: BesoinDate): void {
  const cellule = cellules.get(besoin.date) ?? { quantite: "0", heures: [] };
  cellule.quantite = dec(cellule.quantite).add(dec(besoin.quantite ?? "0")).toFixed();
  if (besoin.heureLimite !== null && !cellule.heures.includes(besoin.heureLimite)) {
    cellule.heures.push(besoin.heureLimite);
    cellule.heures.sort();
  }
  cellules.set(besoin.date, cellule);
}

/**
 * L'arbre carrière → article → chantier.
 *
 * **Une livraison du groupe sans carrière désignée n'est pas écartée** : elle est
 * regroupée sous « carrière à désigner ». C'est l'état à remplir que l'écran de
 * contre-étude signale déjà, et le faire disparaître ici priverait le responsable
 * carrières du seul endroit où il verrait qu'on attend une décision.
 */
export function construireLignes(
  besoins: readonly BesoinDate[],
  carrieres: readonly Carriere[],
  chantiers: readonly ChantierAuPlan[],
): readonly LigneCarriere[] {
  const denominations = new Map(carrieres.map((c) => [c.code, c.denomination]));
  const connus = new Map(chantiers.map((c) => [c.chantierId, c]));

  type BrouillonArticle = {
    code: string;
    libelle: string;
    unite: string;
    cellules: Map<string, Cellule>;
    chantiers: Map<string, { apo: string; nom: string; cellules: Map<string, Cellule> }>;
  };
  const carrieresVues = new Map<
    string,
    { code: string; libelle: string; cellules: Map<string, Cellule>; articles: Map<string, BrouillonArticle> }
  >();

  for (const besoin of besoins) {
    const code = besoin.carriereCode ?? "";
    const carriere = carrieresVues.get(code) ?? {
      code,
      libelle:
        code === ""
          ? "carrière à désigner"
          : (denominations.get(code) ?? "carrière inconnue du référentiel"),
      cellules: new Map<string, Cellule>(),
      articles: new Map<string, BrouillonArticle>(),
    };
    cumuler(carriere.cellules, besoin);

    const article = carriere.articles.get(besoin.code) ?? {
      code: besoin.code,
      libelle: besoin.libelle,
      unite: besoin.unite,
      cellules: new Map<string, Cellule>(),
      chantiers: new Map<string, { apo: string; nom: string; cellules: Map<string, Cellule> }>(),
    };
    cumuler(article.cellules, besoin);

    const chantier = connus.get(besoin.chantierId);
    const ligne = article.chantiers.get(besoin.chantierId) ?? {
      apo: chantier?.apo ?? "?",
      nom: chantier?.nom ?? "",
      cellules: new Map<string, Cellule>(),
    };
    cumuler(ligne.cellules, besoin);

    article.chantiers.set(besoin.chantierId, ligne);
    carriere.articles.set(besoin.code, article);
    carrieresVues.set(code, carriere);
  }

  return [...carrieresVues.values()]
    // Les carrières nommées d'abord, « à désigner » en dernier : c'est un reste à
    // traiter, pas une carrière.
    .sort((a, b) => (a.code === "" ? 1 : b.code === "" ? -1 : a.code.localeCompare(b.code, "fr")))
    .map((carriere) => ({
      cle: carriere.code,
      code: carriere.code === "" ? "—" : carriere.code,
      libelle: carriere.libelle,
      cellules: carriere.cellules,
      articles: [...carriere.articles.values()]
        .sort((a, b) => a.code.localeCompare(b.code, "fr", { numeric: true }))
        .map((article) => ({
          code: article.code,
          libelle: article.libelle,
          unite: article.unite,
          cellules: article.cellules,
          chantiers: [...article.chantiers.entries()]
            .map(([chantierId, ligne]) => ({ chantierId, ...ligne }))
            .sort((a, b) => a.apo.localeCompare(b.apo)),
        })),
    }));
}

/** La somme d'une plage de jours sur une ligne. `null` quand elle ne porte rien. */
function totalDe(cellules: ReadonlyMap<string, Cellule>, jours: readonly string[]): string | null {
  let somme = dec(0);
  let porte = false;
  for (const jour of jours) {
    const cellule = cellules.get(jour);
    if (cellule === undefined) continue;
    porte = true;
    somme = somme.add(dec(cellule.quantite));
  }
  return porte ? somme.toFixed() : null;
}

/* ---------------------------------------------------------------------------
 * Le rendu
 * ------------------------------------------------------------------------- */

export function TableauCarrieres({
  besoins,
  carrieres,
  chantiers,
  aujourdhui,
}: {
  besoins: readonly BesoinDate[];
  carrieres: readonly Carriere[];
  chantiers: readonly ChantierAuPlan[];
  /** Le jour du SERVEUR : il décide des semaines ouvertes et de la colonne marquée. */
  aujourdhui: string;
}): JSX.Element {
  const [ouvertes, setOuvertes] = useState<ReadonlySet<string> | null>(null);
  const [articlesOuverts, setArticlesOuverts] = useState<ReadonlySet<string>>(new Set());
  const [carrieresFermees, setCarrieresFermees] = useState<ReadonlySet<string>>(new Set());

  const jours = useMemo(() => {
    // Les jours qui portent quelque chose, et eux seuls : une carrière ne travaille pas
    // les jours où rien n'est attendu, et cent colonnes vides noieraient les trois qui
    // comptent. Le calendrier a déjà écarté week-ends et fériés en amont.
    const vus = new Set(besoins.map((b) => b.date));
    return [...vus].sort();
  }, [besoins]);

  const semainesOuvertes = ouvertes ?? semainesParDefaut(aujourdhui);
  const colonnes = useMemo(
    () => colonnesDe(jours, semainesOuvertes),
    [jours, semainesOuvertes],
  );
  const lignes = useMemo(
    () => construireLignes(besoins, carrieres, chantiers),
    [besoins, carrieres, chantiers],
  );

  const basculerSemaine = (lundi: string): void => {
    const suivant = new Set(semainesOuvertes);
    if (!suivant.delete(lundi)) suivant.add(lundi);
    setOuvertes(suivant);
  };

  const basculer = (
    cle: string,
    ensemble: ReadonlySet<string>,
    poser: (v: ReadonlySet<string>) => void,
  ): void => {
    const suivant = new Set(ensemble);
    if (!suivant.delete(cle)) suivant.add(cle);
    poser(suivant);
  };

  if (lignes.length === 0) {
    return (
      <p className="etat">
        Aucun matériau du groupe attendu sur cette période. Les livraisons d'un fournisseur
        externe ne concernent pas les carrières : elles restent au tableau de bord transport.
      </p>
    );
  }

  return (
    <div className="plan__defilement">
      <table className="carrieres">
        <thead>
          <tr>
            <th className="carrieres__intitule">Carrière · article · chantier</th>
            {colonnes.map((colonne) => {
              if (colonne.genre === "jour") {
                return (
                  <th
                    key={colonne.cle}
                    className={`carrieres__jour${colonne.cle === aujourdhui ? " carrieres__jour--aujourdhui" : ""}`}
                    title={colonne.cle}
                  >
                    {formaterDateCourte(colonne.cle)}
                  </th>
                );
              }
              if (colonne.genre === "semaine") {
                const lundi = colonne.cle.slice(2);
                const ouverte = semainesOuvertes.has(lundi);
                return (
                  <th key={colonne.cle} className="carrieres__semaine">
                    {/*
                      L'en-tête de semaine est le bouton qui l'ouvre : c'est là qu'on
                      regarde quand on veut le détail, et un bouton posé ailleurs
                      obligerait à faire l'aller-retour entre deux endroits de l'écran.
                    */}
                    <button
                      type="button"
                      className="lien"
                      title={
                        ouverte
                          ? "Replier cette semaine : n'en garder que le total"
                          : "Déplier cette semaine, jour par jour"
                      }
                      onClick={() => {
                        basculerSemaine(lundi);
                      }}
                    >
                      {ouverte ? "▾ " : "▸ "}
                      {colonne.libelle}
                    </button>
                  </th>
                );
              }
              return (
                <th
                  key={colonne.cle}
                  className={colonne.genre === "mois" ? "carrieres__mois" : "carrieres__total"}
                >
                  {colonne.libelle}
                </th>
              );
            })}
          </tr>
        </thead>

        {lignes.map((carriere) => {
          const fermee = carrieresFermees.has(carriere.cle);
          return (
            <tbody key={carriere.cle}>
              <tr className="carrieres__ligne-carriere">
                <th className="carrieres__intitule" scope="row">
                  <button
                    type="button"
                    className="lien"
                    onClick={() => {
                      basculer(carriere.cle, carrieresFermees, setCarrieresFermees);
                    }}
                  >
                    {fermee ? "▸" : "▾"} <span className="code">{carriere.code}</span>{" "}
                    {carriere.libelle}
                  </button>
                </th>
                {colonnes.map((colonne) => (
                  <CelluleTotal
                    key={colonne.cle}
                    colonne={colonne}
                    valeur={totalDe(carriere.cellules, colonne.jours)}
                  />
                ))}
              </tr>

              {!fermee &&
                carriere.articles.map((article) => {
                  const cle = `${carriere.cle}#${article.code}`;
                  const ouvert = articlesOuverts.has(cle);
                  return (
                    <Fragment key={cle}>
                      <tr className="carrieres__ligne-article">
                        <th className="carrieres__intitule" scope="row">
                          <button
                            type="button"
                            className="lien"
                            title="Déplier le détail par chantier"
                            onClick={() => {
                              basculer(cle, articlesOuverts, setArticlesOuverts);
                            }}
                          >
                            {ouvert ? "▾" : "▸"} <span className="code">{article.code}</span>{" "}
                            {article.libelle}
                            {article.unite === "" ? "" : ` (${article.unite})`}
                          </button>
                        </th>
                        {colonnes.map((colonne) => (
                          <CelluleTotal
                            key={colonne.cle}
                            colonne={colonne}
                            valeur={totalDe(article.cellules, colonne.jours)}
                          />
                        ))}
                      </tr>

                      {ouvert &&
                        article.chantiers.map((chantier) => (
                          <tr key={chantier.chantierId} className="carrieres__ligne-chantier">
                            <th className="carrieres__intitule" scope="row">
                              <span className="code">{chantier.apo}</span> {chantier.nom}
                            </th>
                            {colonnes.map((colonne) => (
                              <CelluleDetail
                                key={colonne.cle}
                                colonne={colonne}
                                cellules={chantier.cellules}
                              />
                            ))}
                          </tr>
                        ))}
                    </Fragment>
                  );
                })}
            </tbody>
          );
        })}
      </table>
    </div>
  );
}

/** Une cellule agrégée : le volume seul. */
function CelluleTotal({
  colonne,
  valeur,
}: {
  colonne: Colonne;
  valeur: string | null;
}): JSX.Element {
  return (
    <td className={classeDe(colonne)}>
      {valeur === null ? "" : formaterNombre(valeur, 2)}
    </td>
  );
}

/**
 * Une cellule de chantier : le volume, et l'heure à côté.
 *
 * **L'heure n'apparaît que sur le détail**, jamais sur un total : deux chantiers servis
 * l'un à 7 h et l'autre à 14 h n'ont pas d'heure commune, et en afficher une donnerait
 * une consigne que personne n'a posée. Sur les colonnes de semaine et de mois, l'heure
 * disparaît aussi — une échéance appartient à un jour.
 */
function CelluleDetail({
  colonne,
  cellules,
}: {
  colonne: Colonne;
  cellules: ReadonlyMap<string, Cellule>;
}): JSX.Element {
  const valeur = totalDe(cellules, colonne.jours);
  const heures =
    colonne.genre === "jour" ? (cellules.get(colonne.cle)?.heures ?? []) : [];
  return (
    <td className={classeDe(colonne)}>
      {valeur === null ? "" : formaterNombre(valeur, 2)}
      {heures.length > 0 && <span className="carrieres__heure"> {heures.join(" ")}</span>}
    </td>
  );
}

function classeDe(colonne: Colonne): string {
  if (colonne.genre === "jour") return "carrieres__jour";
  if (colonne.genre === "semaine") return "carrieres__semaine";
  return colonne.genre === "mois" ? "carrieres__mois" : "carrieres__total";
}
